import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const useScrolled = (threshold = 20) => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
};

const useScrollToSection = () => {
  const location = useLocation();
  return useCallback(
    (id) => {
      if (location.pathname !== "/") {
        window.location.href = `/#${id}`;
        return;
      }
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [location.pathname]
  );
};

const Brand = ({ inverse }) => (
  <Link to="/" data-testid="logo-link" className="flex items-center gap-3">
    <div className={`w-10 h-10 flex items-center justify-center border ${inverse ? "border-white" : "border-[#1A1F24]"} transition-colors`}>
      <span className={`font-serif text-xl ${inverse ? "text-white" : "text-[#1A1F24]"}`}>H</span>
    </div>
    <div className="leading-tight">
      <div className={`font-serif text-base sm:text-lg tracking-wide ${inverse ? "text-white" : "text-[#1A1F24]"}`}>
        HARD ROCK
      </div>
      <div className={`text-[10px] uppercase tracking-[0.3em] ${inverse ? "text-white/80" : "text-[#4A5568]"}`}>
        Members
      </div>
    </div>
  </Link>
);

const DesktopNav = ({ items, scrolled, onNavigate }) => (
  <nav className="hidden lg:flex items-center gap-10">
    {items.map((item) => {
      const className = `text-sm tracking-wide transition-colors hover:text-[#C6A87C] ${
        scrolled ? "text-[#1A1F24]" : "text-white"
      }`;
      if (item.to) {
        return (
          <Link
            key={item.id}
            to={item.to}
            data-testid={`nav-${item.id}`}
            className={className}
          >
            {item.label}
          </Link>
        );
      }
      return (
        <button
          key={item.id}
          data-testid={`nav-${item.id}`}
          onClick={() => onNavigate(item.id)}
          className={className}
        >
          {item.label}
        </button>
      );
    })}
  </nav>
);

const MobileMenu = ({ items, onNavigate, bookLabel }) => (
  <div className="lg:hidden bg-white border-t border-[#EAE6DF]" data-testid="mobile-menu">
    <div className="px-6 py-6 flex flex-col gap-4">
      {items.map((item) => {
        if (item.to) {
          return (
            <Link
              key={item.id}
              to={item.to}
              data-testid={`mobile-nav-${item.id}`}
              className="text-left text-sm text-[#1A1F24] py-2"
            >
              {item.label}
            </Link>
          );
        }
        return (
          <button
            key={item.id}
            data-testid={`mobile-nav-${item.id}`}
            onClick={() => onNavigate(item.id)}
            className="text-left text-sm text-[#1A1F24] py-2"
          >
            {item.label}
          </button>
        );
      })}
      <button
        data-testid="mobile-book-btn"
        onClick={() => onNavigate("booking")}
        className="bg-[#2E4F4F] text-white py-3 text-xs uppercase tracking-[0.2em] mt-2"
      >
        {bookLabel}
      </button>
    </div>
  </div>
);

const Header = () => {
  const { lang, toggle, t } = useLang();
  const scrolled = useScrolled();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const scrollTo = useScrollToSection();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  const navItems = [
    { id: "services", label: t.nav.services },
    { id: "catalog", label: t.nav.catalog, to: "/catalog" },
    { id: "hotels", label: t.nav.hotels, to: "/hotels" },
    { id: "fleet", label: t.nav.fleet },
    { id: "about", label: t.nav.about },
    { id: "contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-[#EAE6DF]"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-12 h-20 flex items-center justify-between">
        <Brand inverse={!scrolled} />
        <DesktopNav items={navItems} scrolled={scrolled} onNavigate={scrollTo} />

        <div className="flex items-center gap-3">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className={`flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-[0.2em] border transition-colors ${
              scrolled
                ? "border-[#1A1F24] text-[#1A1F24] hover:bg-[#1A1F24] hover:text-white"
                : "border-white text-white hover:bg-white hover:text-[#1A1F24]"
            }`}
          >
            <Globe className="w-3.5 h-3.5" />
            {lang === "en" ? "ES" : "EN"}
          </button>

          <button
            data-testid="header-book-btn"
            onClick={() => scrollTo("booking")}
            className="hidden sm:inline-flex bg-[#2E4F4F] hover:bg-[#1F3636] text-white px-6 py-3 text-xs uppercase tracking-[0.2em] transition-colors"
          >
            {t.nav.bookNow}
          </button>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((prev) => !prev)}
            className={`lg:hidden p-2 ${scrolled ? "text-[#1A1F24]" : "text-white"}`}
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && <MobileMenu items={navItems} onNavigate={scrollTo} bookLabel={t.nav.bookNow} />}
    </header>
  );
};

export default Header;
